import React, { useEffect, useRef, useState } from 'react';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  useTable,
  usePagination,
  useRowSelect,
  TableOptions,
  useSortBy,
} from 'react-table';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Service from 'src/setup/network';

interface IProps {
  tableData: any[];
  tableColumn: any;
  text?: string;
  onDragEnd?: any;
  pagination?: boolean;
  onSelectRow?: (row: any) => void;
  selectedRows?: Set<number>;
  setPageSizes?: any;
  setPageIndex?: any;
}

interface MyTableOptions<D extends object> extends TableOptions<D> {
  autoResetPage?: boolean;
}

const DragableTable: React.FC<IProps> = ({
  tableData,
  tableColumn,
  pagination,
  onDragEnd,
  setPageSizes,
  setPageIndex,
}) => {
  const data = React.useMemo(() => (tableData ? tableData : []), [tableData]);
  const columns = React.useMemo(() => tableColumn, []);
  const navigate = useNavigate();
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    // Calculate the original widths of td elements when the data changes
    const rowElement = document.querySelector('tbody tr');
    if (rowElement) {
      const widths = Array.from(rowElement.children).map(
        (td: Element) => (td as HTMLElement).offsetWidth
      );
      setColumnWidths(widths);
    }
  }, [data]);

  const {
    headerGroups,
    getTableBodyProps,
    page,
    prepareRow,
    getTableProps,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize },
    setData,
  }: any = useTable(
    {
      data,
      columns,
      autoResetPage: false,
    } as MyTableOptions<any>,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns: any) => {
        return [...columns];
      });
    }
  );
  useEffect(() => {
    setPageSizes(pageSize);
    setPageIndex(pageIndex);
  }, [pageSize, pageIndex]);

  return (
    <div>
      <div className="mt-5 rounded-xl border border-border-color-dark overflow-x-hidden relative  w-full">
        <DragDropContext onDragEnd={onDragEnd}>
          <table
            {...getTableProps()}
            className="w-full text-left text-text-black "
          >
            <thead className="font-semibold text-black border-b border-border-color-dark">
              {headerGroups.map((headerGroup: any, i: number) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column: any, idx: number) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())} // Add getSortByToggleProps() here
                      key={idx}
                      scope="col"
                      className="py-3 px-4 text-sm whitespace-nowrap"
                    >
                      <div className="flex items-center">
                        <span>{column.render('Header')}</span>
                        <span className="ml-2">
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <AiOutlineArrowUp />
                            ) : (
                              <AiOutlineArrowDown />
                            )
                          ) : null}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <Droppable droppableId="table">
              {(provided) => (
                <tbody
                  {...getTableBodyProps()}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="bg-primary-light w-full"
                >
                  {page.map((row: any, i: number) => {
                    prepareRow(row);
                    return (
                      <Draggable key={row.id} draggableId={row.id} index={i}>
                        {(provided) => (
                          <tr
                            {...row.getRowProps()}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            key={i}
                            className="bg-pure-white relative table-row max-w-[max-content] border-b border-border-color-dark hover:bg-light-grey dark:hover:bg-light-grey dark:hover:bg-opacity-20 cursor-pointer  text-base "
                          >
                            {row.cells.map((cell: any, idx: number) => {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  key={idx}
                                  className={`py-3 px-4 table-cell min-w-[${
                                    columnWidths[idx] + 'px' || 'auto'
                                  }] max-w-[25%!important] w-[max-content!important]  ${
                                    idx === 0 && 'draggable-row '
                                  }`}
                                  style={{
                                    minWidth:
                                      columnWidths[idx] + 'px' || 'auto',
                                  }}
                                >
                                  {cell.render('Cell')}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </table>
        </DragDropContext>

        {pagination && (
          <div className=" bg-white p-5 flex justify-between ">
            <div className="flex items-center space-x-5">
              <div>
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className="bg-almost-white rounded-md h-6 w-6 pl-1 text-normal  text-black"
                >
                  <MdKeyboardArrowLeft />
                </button>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className="bg-almost-white rounded-md h-6 w-6 pl-1  text-normal  ml-3 text-black"
                >
                  <MdKeyboardArrowRight />
                </button>
                <span className="ml-5 text-black  ">
                  Page {pageIndex + 1} of {pageOptions.length}
                </span>
              </div>
            </div>
            <div className="text-black">
              <span className="text-sm text-text-black">Results per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
                className="font-normal px-1  text-sm rounded py-1 focus:outline-0 ml-2 bg-[white] border border-border-color-dark "
              >
                {[1, 5, 10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize} className="text-sm">
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DragableTable);
